import {
  ZadDiscountType,
  ZadPurchaseImportDiscount,
  ZadPurchaseImportItem,
  ZadPurchaseImportPayload,
} from '../../../models/zad'
import {formatZadTnd} from './productHelpers'
import {isValidZadStampAmountNumber, previewZadPurchaseTotals} from './purchaseHelpers'

export const ZAD_PURCHASE_IMPORT_ALLOWED_ROOT = [
  'supplierCode',
  'supplierDocumentNumber',
  'purchaseDate',
  'items',
  'globalDiscount',
  'stampAmount',
  'notes',
] as const

export const ZAD_PURCHASE_IMPORT_ALLOWED_ITEM = [
  'productCode',
  'barcode',
  'quantity',
  'unitPrice',
  'discount',
  'vatRate',
  'purchaseUnit',
  'unitsPerPackage',
] as const

export const ZAD_PURCHASE_IMPORT_CALCULATED_FIELDS = [
  'lineSubtotal',
  'lineDiscountAmount',
  'netUnitPrice',
  'lineTotal',
  'allocatedGlobalDiscount',
  'taxableNetHT',
  'vatAmount',
  'lineTotalTTC',
  'netHT',
  'vatTotal',
  'totalTTCBeforeStamp',
  'amountPayable',
  'itemDiscountTotal',
  'totalDiscount',
  'subtotal',
  'total',
] as const

export const ZAD_PURCHASE_IMPORT_ALLOWED_DISCOUNT = ['type', 'value'] as const

export const ZAD_PURCHASE_IMPORT_STOCK_FIELDS = [
  'stock',
  'currentStock',
  'initialStock',
  'availableStock',
  'stockMovement',
  'signedQuantity',
  'quantityOnHand',
] as const

export const ZAD_PURCHASE_IMPORT_PAYMENT_FIELDS = [
  'paidAmount',
  'amountPaid',
  'payment',
  'paymentMethod',
  'paymentStatus',
  'remaining',
  'remainingAmount',
  'debt',
  'outstanding',
] as const

export const ZAD_PURCHASE_IMPORT_DISCOUNT_TYPES = ['NONE', 'PERCENT', 'FIXED'] as const

export type ZadPurchaseImportParseErrorCode =
  | 'EMPTY_FILE'
  | 'UNSUPPORTED_FILE'
  | 'INVALID_JSON'
  | 'INVALID_ROOT'
  | 'MISSING_SUPPLIER_CODE'
  | 'MISSING_PURCHASE_DATE'
  | 'MISSING_ITEMS'
  | 'EMPTY_ITEMS'
  | 'INVALID_ITEM'
  | 'MISSING_PRODUCT_IDENTIFIER'
  | 'INVALID_QUANTITY'
  | 'INVALID_UNIT_PRICE'
  | 'INVALID_BARCODE'
  | 'INVALID_DISCOUNT'
  | 'INVALID_STAMP_AMOUNT'
  | 'UNSUPPORTED_FIELD'
  | 'UNSUPPORTED_STOCK_FIELD'
  | 'UNSUPPORTED_PAYMENT_FIELD'
  | 'CALCULATED_FIELD'
  | 'INVALID_VAT'

export type ZadPurchaseImportParseResult =
  | {ok: true; payload: ZadPurchaseImportPayload; fileName: string}
  | {
      ok: false
      errorCode: ZadPurchaseImportParseErrorCode
      rowIndex?: number
      field?: string
      productCode?: string
      barcode?: string
    }

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function requiredString(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== ''
}

function isPositiveInteger(value: unknown): boolean {
  if (typeof value === 'number') return Number.isInteger(value) && value > 0
  if (typeof value === 'string' && value.trim() !== '') {
    return /^\d+$/.test(value.trim()) && Number(value.trim()) > 0
  }
  return false
}

function isNonNegativeNumber(value: unknown): boolean {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0
  if (typeof value === 'string' && value.trim() !== '') {
    const numeric = Number(value.replace(',', '.'))
    return Number.isFinite(numeric) && numeric >= 0
  }
  return false
}

function includesField(list: readonly string[], field: string) {
  return list.includes(field)
}

function readImportVatRate(value: unknown): {ok: true; value: number} | {ok: false} {
  if (value === undefined || value === '') return {ok: true, value: 0}
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100) {
    return {ok: true, value}
  }
  return {ok: false}
}

export function isZadPurchaseImportStockField(field: string) {
  return includesField(ZAD_PURCHASE_IMPORT_STOCK_FIELDS, field)
}

export function isZadPurchaseImportPaymentField(field: string) {
  return includesField(ZAD_PURCHASE_IMPORT_PAYMENT_FIELDS, field)
}

function unsupportedErrorCode(field: string): ZadPurchaseImportParseErrorCode {
  const name = field.split('.').pop() || field
  if (includesField(ZAD_PURCHASE_IMPORT_CALCULATED_FIELDS, name)) return 'CALCULATED_FIELD'
  if (isZadPurchaseImportStockField(field)) return 'UNSUPPORTED_STOCK_FIELD'
  if (isZadPurchaseImportPaymentField(field)) return 'UNSUPPORTED_PAYMENT_FIELD'
  return 'UNSUPPORTED_FIELD'
}

function findUnsupportedField(row: Record<string, unknown>, allowed: readonly string[]) {
  return Object.keys(row).find((field) => !allowed.includes(field))
}

function validateDiscount(
  raw: unknown,
  field: string
): {ok: true} | {ok: false; errorCode: ZadPurchaseImportParseErrorCode; field: string} {
  if (raw === undefined || raw === null || raw === '') return {ok: true}
  if (!isPlainObject(raw)) {
    return {ok: false, errorCode: 'INVALID_DISCOUNT', field}
  }
  const extra = findUnsupportedField(raw, ZAD_PURCHASE_IMPORT_ALLOWED_DISCOUNT)
  if (extra) {
    return {ok: false, errorCode: unsupportedErrorCode(extra), field: `${field}.${extra}`}
  }
  const type = String(raw.type || '').trim().toUpperCase()
  if (!includesField(ZAD_PURCHASE_IMPORT_DISCOUNT_TYPES, type)) {
    return {ok: false, errorCode: 'INVALID_DISCOUNT', field: `${field}.type`}
  }
  if (type !== 'NONE' && !isNonNegativeNumber(raw.value)) {
    return {ok: false, errorCode: 'INVALID_DISCOUNT', field: `${field}.value`}
  }
  return {ok: true}
}

function sanitizeDiscount(raw: unknown): ZadPurchaseImportDiscount | undefined {
  if (!isPlainObject(raw)) return undefined
  if (raw.type === undefined && raw.value === undefined) return undefined
  const discount: ZadPurchaseImportDiscount = {
    type: typeof raw.type === 'string' ? raw.type : String(raw.type ?? 'NONE'),
    value: typeof raw.value === 'number' || typeof raw.value === 'string' ? raw.value : 0,
  }
  return discount
}

export function sanitizeZadPurchaseImportItem(row: unknown): ZadPurchaseImportItem {
  const source = isPlainObject(row) ? row : {}
  const item: ZadPurchaseImportItem = {
    quantity:
      typeof source.quantity === 'number' || typeof source.quantity === 'string'
        ? source.quantity
        : '',
    unitPrice:
      typeof source.unitPrice === 'number' || typeof source.unitPrice === 'string'
        ? source.unitPrice
        : '',
  }
  if (typeof source.productCode === 'string') item.productCode = source.productCode
  if (typeof source.barcode === 'string') item.barcode = source.barcode
  const discount = sanitizeDiscount(source.discount)
  if (discount) item.discount = discount
  const vatRate = readImportVatRate(source.vatRate)
  if (vatRate.ok) item.vatRate = vatRate.value
  const purchaseUnit = String(source.purchaseUnit || '').trim().toUpperCase()
  if (purchaseUnit === 'UNIT' || purchaseUnit === 'PACKAGE') {
    item.purchaseUnit = purchaseUnit
  }
  if (source.unitsPerPackage != null && source.unitsPerPackage !== '') {
    const units = Number(source.unitsPerPackage)
    if (Number.isInteger(units)) item.unitsPerPackage = units
  }
  return item
}

export function sanitizeZadPurchaseImportPayload(raw: unknown): ZadPurchaseImportPayload {
  const source = isPlainObject(raw) ? raw : {}
  const payload: ZadPurchaseImportPayload = {
    supplierCode: typeof source.supplierCode === 'string' ? source.supplierCode : '',
    purchaseDate: typeof source.purchaseDate === 'string' ? source.purchaseDate : String(source.purchaseDate || ''),
    items: Array.isArray(source.items) ? source.items.map(sanitizeZadPurchaseImportItem) : [],
  }
  if (typeof source.supplierDocumentNumber === 'string') {
    payload.supplierDocumentNumber = source.supplierDocumentNumber
  }
  const globalDiscount = sanitizeDiscount(source.globalDiscount)
  if (globalDiscount) payload.globalDiscount = globalDiscount
  if (typeof source.stampAmount === 'number') payload.stampAmount = source.stampAmount
  if (typeof source.notes === 'string') payload.notes = source.notes
  return payload
}

function validateItem(row: unknown): {
  ok: boolean
  errorCode?: ZadPurchaseImportParseErrorCode
  field?: string
  productCode?: string
  barcode?: string
} {
  if (!isPlainObject(row)) {
    return {ok: false, errorCode: 'INVALID_ITEM'}
  }
  const productCode = typeof row.productCode === 'string' ? row.productCode : undefined
  const barcode = typeof row.barcode === 'string' ? row.barcode : undefined
  const extra = findUnsupportedField(row, ZAD_PURCHASE_IMPORT_ALLOWED_ITEM)
  if (extra) {
    return {
      ok: false,
      errorCode: unsupportedErrorCode(extra),
      field: extra,
      productCode,
      barcode,
    }
  }
  if (row.barcode !== undefined && row.barcode !== null && row.barcode !== '' && typeof row.barcode !== 'string') {
    return {ok: false, errorCode: 'INVALID_BARCODE', field: 'barcode', productCode}
  }
  const hasProductCode = requiredString(row.productCode)
  const hasBarcode = typeof row.barcode === 'string' && row.barcode.trim() !== ''
  if (!hasProductCode && !hasBarcode) {
    return {ok: false, errorCode: 'MISSING_PRODUCT_IDENTIFIER', field: 'productCode', barcode}
  }
  if (!isPositiveInteger(row.quantity)) {
    return {ok: false, errorCode: 'INVALID_QUANTITY', field: 'quantity', productCode, barcode}
  }
  if (!isNonNegativeNumber(row.unitPrice) || row.unitPrice === '' || row.unitPrice == null) {
    return {ok: false, errorCode: 'INVALID_UNIT_PRICE', field: 'unitPrice', productCode, barcode}
  }
  const discountResult = validateDiscount(row.discount, 'discount')
  if (!discountResult.ok) {
    return {...discountResult, productCode, barcode}
  }
  if (!readImportVatRate(row.vatRate).ok) {
    return {ok: false, errorCode: 'INVALID_VAT', field: 'vatRate', productCode, barcode}
  }
  if (row.purchaseUnit != null && row.purchaseUnit !== '') {
    const unit = String(row.purchaseUnit).trim().toUpperCase()
    if (unit !== 'UNIT' && unit !== 'PACKAGE') {
      return {ok: false, errorCode: 'INVALID_ITEM', field: 'purchaseUnit', productCode, barcode}
    }
    if (unit === 'PACKAGE') {
      const units = Number(row.unitsPerPackage)
      if (!Number.isInteger(units) || units < 2) {
        return {ok: false, errorCode: 'INVALID_QUANTITY', field: 'unitsPerPackage', productCode, barcode}
      }
    }
  }
  return {ok: true}
}

export function parseZadPurchaseImportJson(
  text: string,
  fileName = ''
): ZadPurchaseImportParseResult {
  if (text == null || String(text).trim() === '') {
    return {ok: false, errorCode: 'EMPTY_FILE'}
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return {ok: false, errorCode: 'INVALID_JSON'}
  }

  if (!isPlainObject(parsed)) {
    return {ok: false, errorCode: 'INVALID_ROOT'}
  }

  const extraRoot = findUnsupportedField(parsed, ZAD_PURCHASE_IMPORT_ALLOWED_ROOT)
  if (extraRoot) {
    return {ok: false, errorCode: unsupportedErrorCode(extraRoot), field: extraRoot}
  }

  if (!requiredString(parsed.supplierCode)) {
    return {ok: false, errorCode: 'MISSING_SUPPLIER_CODE', field: 'supplierCode'}
  }
  if (parsed.purchaseDate === undefined || parsed.purchaseDate === null || parsed.purchaseDate === '') {
    return {ok: false, errorCode: 'MISSING_PURCHASE_DATE', field: 'purchaseDate'}
  }
  if (parsed.items === undefined) {
    return {ok: false, errorCode: 'MISSING_ITEMS', field: 'items'}
  }
  if (!Array.isArray(parsed.items)) {
    return {ok: false, errorCode: 'MISSING_ITEMS', field: 'items'}
  }
  if (parsed.items.length === 0) {
    return {ok: false, errorCode: 'EMPTY_ITEMS', field: 'items'}
  }

  const globalDiscountResult = validateDiscount(parsed.globalDiscount, 'globalDiscount')
  if (!globalDiscountResult.ok) {
    return {
      ok: false,
      errorCode: globalDiscountResult.errorCode,
      field: globalDiscountResult.field,
    }
  }

  if (parsed.stampAmount !== undefined) {
    if (!isValidZadStampAmountNumber(parsed.stampAmount)) {
      return {ok: false, errorCode: 'INVALID_STAMP_AMOUNT', field: 'stampAmount'}
    }
  }

  for (let index = 0; index < parsed.items.length; index += 1) {
    const rowResult = validateItem(parsed.items[index])
    if (!rowResult.ok) {
      return {
        ok: false,
        errorCode: rowResult.errorCode || 'INVALID_ITEM',
        rowIndex: index,
        field: rowResult.field,
        productCode: rowResult.productCode,
        barcode: rowResult.barcode,
      }
    }
  }

  return {
    ok: true,
    payload: sanitizeZadPurchaseImportPayload(parsed),
    fileName,
  }
}

export function formatZadPurchaseImportDiscount(discount?: ZadPurchaseImportDiscount) {
  if (!discount) return '—'
  const type = String(discount.type || 'NONE').trim().toUpperCase()
  const value = discount.value
  if (type === 'PERCENT') return `${value}%`
  if (type === 'FIXED') {
    const numeric = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
    return Number.isFinite(numeric) ? formatZadTnd(numeric) : '—'
  }
  return '—'
}

export function estimateZadPurchaseImportPreview(payload: ZadPurchaseImportPayload) {
  const lineGrossAmounts = payload.items.map((item) => {
    const quantity = Number(item.quantity)
    const unitPrice = Number(String(item.unitPrice).replace(',', '.'))
    if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) return null
    return quantity * unitPrice
  })
  const totals = previewZadPurchaseTotals(
    payload.items.map((item) => ({
      quantity: item.quantity,
      listUnitPrice: item.unitPrice,
      discountType: String(item.discount?.type || 'NONE').toUpperCase() as ZadDiscountType,
      discountValue: item.discount?.value ?? 0,
      vatRate: typeof item.vatRate === 'number' ? item.vatRate : 0,
    })),
    String(payload.globalDiscount?.type || 'NONE').toUpperCase() as ZadDiscountType,
    payload.globalDiscount?.value ?? 0,
    payload.stampAmount ?? 0
  )
  return {lineGrossAmounts, totals}
}

export function getZadPurchaseImportExampleJson(): string {
  return `{
  "supplierCode": "SUPPLIER_CODE",
  "supplierDocumentNumber": "FACTURE-001",
  "purchaseDate": "2026-10-04",
  "items": [
    {
      "productCode": "PRODUCT-001",
      "quantity": 10,
      "unitPrice": 5.000,
      "discount": {
        "type": "PERCENT",
        "value": 10
      },
      "vatRate": 19
    }
  ],
  "globalDiscount": {
    "type": "PERCENT",
    "value": 0
  },
  "stampAmount": 0,
  "notes": ""
}
`
}

export function downloadZadPurchaseImportExample() {
  const blob = new Blob([getZadPurchaseImportExampleJson()], {type: 'application/json'})
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'zad-purchase-invoice-example.json'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
